/**
 * Qué se puede ver de un adjunto dentro de la página y cómo se rotula su tipo (ícono de extensión).
 *
 * Sin React ni red: lo usan la lista y el visor, y se prueba aparte.
 */
import {
  MIME_PERMITIDOS,
  MIME_VISIBLES_EN_LINEA,
  esMimePermitido,
  type Adjunto,
} from '@cdtec-backlog/contrato';

/** Cómo muestra el visor un adjunto. `pdf` todavía no se usa (ver `tipoDePrevisualizacion`). */
export type Previsualizacion = 'imagen' | 'texto' | 'pdf' | 'sin-vista';

/** Tope de lo que se lee de un `.txt` o `.csv` para mostrarlo (el resto se descarga). */
export const MAX_BYTES_TEXTO = 200 * 1024;

const MIME_TEXTO = ['text/plain', 'text/csv'];

/**
 * Qué escena usa el visor para este adjunto:
 * - `imagen`: las que la API entrega en línea (`?inline=1`): PNG, JPEG, GIF y WebP.
 * - `texto`: `text/plain` y `text/csv`, leídos como texto (con tope) y mostrados en `<pre>`.
 * - `sin-vista`: el resto (Word, Excel, PowerPoint y, por ahora, PDF): panel con «Descargar».
 *
 * PDF: la API hoy solo entrega en línea las imágenes, así que un PDF no se puede incrustar. Cuando la
 * API lo permita (`?inline=1` para `application/pdf`, con su CSP), basta con llamar con
 * `{ pdfEnLinea: true }` (o cambiar el valor por defecto): el visor ya dibuja el caso `pdf` con un
 * `<iframe>`.
 */
export function tipoDePrevisualizacion(
  adjunto: Pick<Adjunto, 'tipoMime'>,
  { pdfEnLinea = false }: { pdfEnLinea?: boolean } = {},
): Previsualizacion {
  const mime = adjunto.tipoMime;
  if ((MIME_VISIBLES_EN_LINEA as readonly string[]).includes(mime)) return 'imagen';
  if (MIME_TEXTO.includes(mime)) return 'texto';
  if (mime === 'application/pdf' && pdfEnLinea) return 'pdf';
  return 'sin-vista';
}

/** Familia de color del ícono de extensión (cada una tiene su token `--ext-*`). */
export type FamiliaExtension = 'pdf' | 'doc' | 'hoja' | 'presentacion' | 'imagen' | 'otro';

export function familiaDeExtension(tipoMime: string): FamiliaExtension {
  if (tipoMime.startsWith('image/')) return 'imagen';
  if (tipoMime === 'application/pdf') return 'pdf';
  if (tipoMime === 'text/csv' || tipoMime.includes('spreadsheetml')) return 'hoja';
  if (tipoMime.includes('presentationml')) return 'presentacion';
  if (tipoMime.includes('wordprocessingml')) return 'doc';
  return 'otro';
}

/** Extensión para el ícono (en minúsculas; el CSS la pasa a mayúsculas): la del nombre o la del tipo. */
export function extensionDe(adjunto: Pick<Adjunto, 'nombre' | 'tipoMime'>): string {
  const partes = adjunto.nombre.split('.');
  const delNombre = partes.length > 1 ? (partes.pop() ?? '').toLowerCase() : '';
  if (/^[a-z0-9]{1,4}$/.test(delNombre)) return delNombre;
  return esMimePermitido(adjunto.tipoMime)
    ? (MIME_PERMITIDOS[adjunto.tipoMime].extensiones[0] ?? 'arch')
    : 'arch';
}

/** Orden del visor (y de la pantalla): primero las imágenes, después los documentos. */
export function ordenarParaVisor<T extends Pick<Adjunto, 'tipoMime'>>(adjuntos: T[]): T[] {
  const esImagen = (a: T) => tipoDePrevisualizacion(a) === 'imagen';
  return [...adjuntos.filter(esImagen), ...adjuntos.filter((a) => !esImagen(a))];
}
